import { create } from 'zustand';
import type { VespAsset, SearchParams, LibraryStats } from '../types';
import { api } from '../lib/api';

interface AppState {
  // Assets
  assets: VespAsset[];
  totalAssets: number;
  loading: boolean;
  error: string | null;

  // Search
  searchQuery: string;
  filters: SearchParams;
  /** 语义搜索是否可用（daemon 离线/超时时 false，UI 显示提示） */
  semanticUnavailable: boolean;

  // Stats
  stats: LibraryStats | null;

  // Selected asset
  selectedAsset: VespAsset | null;

  // Actions
  fetchAssets: (params?: SearchParams) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: Partial<SearchParams>) => void;
  fetchStats: () => Promise<void>;
  selectAsset: (asset: VespAsset | null) => void;
  toggleFavorite: (id: string) => Promise<void>;
  deleteAsset: (id: string) => Promise<void>;
}

export const useAppStore = create<AppState>((set, get) => ({
  assets: [],
  totalAssets: 0,
  loading: false,
  error: null,
  searchQuery: '',
  filters: { limit: 40, offset: 0 },
  semanticUnavailable: false,
  stats: null,
  selectedAsset: null,

  fetchAssets: async (params) => {
    set({ loading: true, error: null });
    try {
      const state = get();
      const searchParams = params || { ...state.filters, query: state.searchQuery || undefined };

      let result;
      if (state.searchQuery) {
        // 混合搜索：先语义（向量召回，理解自然语言描述），无结果/不可用回退关键词 FTS
        let semanticOk = false;
        try {
          result = await api.search({ ...searchParams, semantic: true, timeoutMs: 8000 } as any);
          semanticOk = true;
          if (!result.data || result.data.length === 0) {
            result = await api.search(searchParams);
          }
        } catch {
          result = await api.search(searchParams);
        }
        set({ semanticUnavailable: !semanticOk, loading: true });
      } else {
        result = await api.listAssets(searchParams);
        set({ semanticUnavailable: false, loading: true });
      }

      set({
        assets: result.data,
        totalAssets: result.total || 0,
        loading: false,
      });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  setSearchQuery: (query) => set({ searchQuery: query }),

  setFilters: (filters) => {
    const current = get().filters;
    set({ filters: { ...current, ...filters } });
  },

  fetchStats: async () => {
    try {
      const result = await api.getStats();
      set({ stats: result.data });
    } catch {
      // Stats are non-critical
    }
  },

  selectAsset: (asset) => set({ selectedAsset: asset }),

  toggleFavorite: async (id) => {
    try {
      await api.toggleFavorite(id);
      const { assets } = get();
      set({
        assets: assets.map(a =>
          a.vesp_id === id ? { ...a, is_favorite: a.is_favorite ? 0 : 1 } : a
        ),
      });
    } catch {
      // Silently fail
    }
  },

  deleteAsset: async (id) => {
    try {
      await api.deleteAsset(id);
      const { assets } = get();
      set({ assets: assets.filter(a => a.vesp_id !== id) });
    } catch {
      // Silently fail
    }
  },
}));
